import React, { useRef, useState, useEffect, useMemo } from "react";
import { Text } from "components/common/Text";
import { componentConfig } from "config/theme";
import { PitchLayout } from "../components/PitchLayout";
import { InfoCard } from "../components/InfoCard";
import { mockEvents } from "./mockChalkboardData";
import {
  CATEGORY_SUBTYPES,
  SUBTYPE_LABELS,
  getShapeByCategory,
  Category,
  SubTypeId,
  ChalkboardEvent,
} from "./utils";
import { EventMarker } from "./components/EventMarker";
import { getTeamColor, toggleSubtype } from "./helper";
import { ShotPaths } from "./components/ShotPaths";
import { DistributionPaths } from "./components/DistributionPaths";
import MinuteSlider from "../xgMap/components/MinuteSlider";

const Chalkboard: React.FC<{ events?: ChalkboardEvent[]; status: any }> = ({
  events,
  status,
}) => {
  const [selectedCategory, setSelectedCategory] = useState<Category>("Shots");
  const subtypes = CATEGORY_SUBTYPES[selectedCategory];
  const [activeSubtypes, setActiveSubtypes] = useState<SubTypeId[]>([
    ...subtypes,
  ]);
  const [tooltip, setTooltip] = useState<ChalkboardEvent | null>(null);

  const [timeCut, setTimeCut] = useState<number>(95);
  const liveMinute = useMemo(() => Number(status?.tm) || 0, [status?.tm]);
  const endMinute = useMemo(() => {
    const taNum = Number(status?.ta);
    return 90 + (Number.isFinite(taNum) && taNum > 0 ? taNum : 0);
  }, [status?.ta]);

  const allEvents = events && events.length > 0 ? events : mockEvents;

  const shownEvents = allEvents.filter((e) => {
    if (e.category !== selectedCategory) return false;
    if (!activeSubtypes.includes(e.subtype)) return false;
    if (typeof e.minute === "number" && e.minute > timeCut) return false;
    return true;
  });

  const layerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setTooltip(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    setActiveSubtypes([...CATEGORY_SUBTYPES[selectedCategory]]);
  }, [selectedCategory]);

  const clamp01 = (v: number) => Math.max(0.02, Math.min(0.98, v));
  const getAnchorForEvent = (event: ChalkboardEvent) => {
    const x = clamp01(event.x);
    const y = clamp01(event.y);
    const horizontalRoom = Math.max(x, 1 - x);
    const verticalRoom = Math.max(y, 1 - y);

    let preferred: "above" | "below" | "left" | "right" = "above";
    if (verticalRoom >= horizontalRoom) {
      preferred = y < 0.5 ? "below" : "above";
    } else {
      preferred = x < 0.5 ? "right" : "left";
    }

    return {
      anchor: { x, y },
      prefer: preferred,
    };
  };

  const tooltipAnchor = tooltip ? getAnchorForEvent(tooltip) : null;

  return (
    <div>
      <div className="flex gap-[1vw] flex-nowrap justify-start overflow-x-auto whitespace-nowrap border-b-2 border-border px-[2vw] py-0">
        {(Object.keys(CATEGORY_SUBTYPES) as Category[]).map((cat) => (
          <button
            key={cat}
            type="button"
            className={`flex-shrink-0 border-b-2 border-transparent px-[1.2vw] py-[0.5vw] transition-colors ${
              selectedCategory === cat
                ? componentConfig.categoryTabsActiveClass
                : componentConfig.categoryTabsInactiveClass
            }`}
            onClick={() => setSelectedCategory(cat)}
            data-testid={`cat-tab-${cat}`}
          >
            <Text
              as="span"
              variant="categoryTabLabel"
              className="font-semibold"
            >
              {cat}
            </Text>
          </button>
        ))}
      </div>

      <div className="flex flex-nowrap gap-[0.7vw] justify-start overflow-x-auto whitespace-nowrap px-[1vw] py-[0.5vw]">
        {subtypes.map((id) => (
          <button
            key={id}
            type="button"
            className={`flex-shrink-0 rounded-[1.2vw] px-[1vw] py-[0.5vw] transition-colors items-center justify-center ${
              activeSubtypes.includes(id)
                ? componentConfig.categorySubtypesActiveClass
                : componentConfig.categorySubtypesInactiveClass
            }`}
            onClick={() => setActiveSubtypes((p) => toggleSubtype(p, id))}
            data-testid={`chip-${id}`}
          >
            <Text
              as="span"
              variant="categorySubtypeLabel"
              className="font-semibold"
            >
              {SUBTYPE_LABELS[id]}
            </Text>
          </button>
        ))}
      </div>

      <PitchLayout>
        <div
          className="absolute inset-0 z-[2]"
          ref={layerRef}
          onMouseDown={(e) => {
            if (e.target === layerRef.current) setTooltip(null);
          }}
        >
          <ShotPaths events={shownEvents} status={status} />
          <DistributionPaths events={shownEvents} status={status} />

          {shownEvents.map((e) => (
            <EventMarker
              key={e.id}
              event={e}
              color={getTeamColor(status, e.team)}
              shape={getShapeByCategory(e.category)}
              onSelect={() => setTooltip(e)}
            />
          ))}

          {tooltip && tooltipAnchor && (
            <InfoCard
              title={`(${tooltip.playerNum}) ${tooltip.playerName} `}
              titleRight={`${tooltip.minute}'`}
              headerBg={getTeamColor(status, tooltip.team)}
              rows={[
                { label: SUBTYPE_LABELS[tooltip.subtype].toUpperCase() },
                { label: tooltip.teamName },
              ]}
              anchor={tooltipAnchor.anchor}
              prefer={tooltipAnchor.prefer}
              containerRef={layerRef}
              withBackdrop
              onClose={() => setTooltip(null)}
            />
          )}
        </div>
      </PitchLayout>

      <MinuteSlider
        liveMinute={liveMinute}
        endMinute={endMinute}
        value={timeCut}
        onChange={(v) => setTimeCut(v)}
        labels={{ left: "0:00", mid: "FH", right: `${endMinute}:00` }}
      />
    </div>
  );
};

export default Chalkboard;
